// @flow
import React from 'react';
import classnames from 'classnames';
import URI from 'urijs';
import { constants } from '@clubmed/utils';

import { convertLocationToURI } from 'Utils/uri/uriUtils';
import { useRouter } from 'Client/router/useRouter';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { ICONS } from 'Components/utils/icon/icons';

type BreadcrumbLineProps = {
  originalUrl: string,
  countryPage?: null | {
    pageCountry: { breadcrumbLabel: string },
    url: string,
  },
  regionPage?: null | {
    pageRegion: { breadcrumbLabel: string },
    url: string,
  },
  resort: { name: string },
};

type BreadcrumbCellProps = { icon?: string, label: string, url?: string, numberOfItems: number };

const BreadcrumbCell = ({ icon, label, url, numberOfItems }: BreadcrumbCellProps) => {
  const maxWidthClass = numberOfItems < 5 ? `max-w-1/${numberOfItems}` : 'max-w-1/4';
  const baseClasses = `border-1 border-gray-light font-bold inline-flex items-center justify-center leading-one mx-1 p-2 rounded-tiny text-sm whitespace-no-wrap flex-grow sm:flex-grow-0 ${maxWidthClass}`;
  return url ? (
    <DynamicLink className={classnames(baseClasses, 'text-gray-darker')} url={url}>
      <span className="max-w-full truncate">
        {icon && (
          <Icon className="rtl:mr-2 overflow-visible" name={icon} width="calc(12rem / 16)" />
        )}
        {label}
      </span>
    </DynamicLink>
  ) : (
    <div className={classnames(baseClasses, 'text-black')}>
      <span className="max-w-full truncate">{label}</span>
    </div>
  );
};

export const ProductBreadcrumbLine = ({
  countryPage,
  originalUrl,
  regionPage,
  resort,
}: BreadcrumbLineProps) => {
  const { location } = useRouter<UrlParams, QueryParams>();
  const origin = new URI(originalUrl).origin().toString();
  const breadcrumbItems = [
    { label: constants.WEBSITE_NAME, url: '/', icon: ICONS.HOME },
    regionPage ? { label: regionPage.pageRegion.breadcrumbLabel, url: regionPage.url } : null,
    countryPage ? { label: countryPage.pageCountry.breadcrumbLabel, url: countryPage.url } : null,
    { label: resort.name, url: '' },
  ].filter(Boolean);

  return (
    <SiteContainer className="Line">
      {[
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems.map((item, index) => {
              return {
                '@type': 'ListItem',
                position: index + 1,
                item: {
                  '@id': item.url
                    ? origin + item.url
                    : convertLocationToURI({ ...location, origin }),
                  name: item.label,
                },
              };
            }),
          }),
        },
      ].map((script, idx) => (
        <script
          key={idx}
          type={script.type}
          dangerouslySetInnerHTML={{ __html: script.innerHTML }}
        />
      ))}
      <div className="flex -mx-1 justify-center md:justify-start">
        {breadcrumbItems.map(item => (
          <BreadcrumbCell {...item} numberOfItems={breadcrumbItems.length} key={item.label} />
        ))}
      </div>
    </SiteContainer>
  );
};
